import React, { ReactNode } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { ConfirmPopup } from 'primereact/confirmpopup';
import Breadcrumb from '../../components/Breadcrumb';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../helpers/StandardEditorContainerComponent';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import { ServiceNames } from '../../constant/ServiceNames';
import ServiceContainer from '../../config/ServiceContainer';
import { query, repository, subscribe } from '@trlogic/client-js';
import CustomLoading from '../../components/custom/CustomLoading';
import CustomTabv2 from '../../components/custom/CustomTabv2';
import CollectionNames from '../../constant/CollectionNames';
import RoleHelper from '../../RoleHelper';
import LanguageUtil from '../../util/LanguageUtil';
import { customConfirm } from '../../components/custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../../components/deploy/UndeployImpactDialog';
import { EnrichedComponentCategory } from '../../domain/category/CategoryOperationDomains';
import ComponentAuditLog from '../../components/ComponentAuditLog';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { enterComponent, leaveComponent } from '../../redux/actions/ComponentActions';
import { clearPage } from '../../redux/actions/PageActions';
import CustomEditorItemComponent from '../../components/custom/CustomEditorItemComponent';
import ComponentName from '../../constant/ComponentName';
import { RootState } from '../../redux/store';
import { changePolicy, changePolicyDependencies, changePolicyDirty, changePolicyIsUpdate, clearPolicy } from '../../redux/actions/PolicyActions';
import { setCraApiVariable, clearAllCraApiVariables } from '../../redux/actions/CraApiVariableActions';
import { PolicyState } from '../../redux/reducers/PolicyReducer';
import { PolicyJsonModel, CraApiVariableSchema } from '../../domain/cra/CraDomains';
import CraService from '../../service/CraService';
import CraTypeRadioMenu from '../../components/cra/policy/CraTypeRadioMenu';
import PolicyAccessComponent from '../../components/cra/policy/access/PolicyAccessComponent';
import { CraType } from '../../constant/Constants';
import ValueFactory from '../../util/ValueFactory';
import CustomSkeleton from '../../components/custom/CustomSkeleton';
import CraListComponent from './CraListComponent';
import ExpressionBridge from '../../util/ExpressionBridge';
import { ResponseFormats } from '../../constant/ResponseFormats';
import DataType from '../../constant/DataType';
import eq = query.eq;

class CraPolicyContainer extends StandardEditorContainerComponent<Props, State, PolicyJsonModel> {
  public state: State = new State();

  private craSubscription: subscribe.Subscription | undefined;

  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly craService: CraService = ServiceContainer.getInstance().get(ServiceNames.CRA);
  private readonly expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.POLICY,
      icon: 'sidebar-policy',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      policy: { policy, isUpdate },
    } = this.props;
    const { loading, policyLoading } = this.state;
    if (loading || policyLoading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={LanguageUtil.get({ id: 'pages.cra.title', defaultMessage: 'Customer Risk Assessment' })}
          description={''}
          status={policy?.stage || 'DRAFT'}
          component={ComponentName.POLICY}
          isUpdate={isUpdate}
          icon={'sidebar-policy'}
          onChangeName={() => {}}
          onChangeDescription={() => {}}
          onSave={() => this.onSave()}
          onDeploy={(e) => this.onDeploy(e)}
          onUndeploy={(e) => this.onUndeploy(e)}
          disableSave={!RoleHelper.canEdit('Cra')}
          disableRemove={true}
          disableNameChange={true}
          disableDescriptionChange={true}
          disableDeploy={!RoleHelper.canDeploy('Cra')}
          disableUndeploy={!RoleHelper.canDeploy('Cra')}
        />
        <div className={'flex flex-row justify-between items-center px-5 pb-5'}>
          <CraTypeRadioMenu onChangeType={this.onChangeType} value={policy.assessor} />
        </div>
        <div className={'flex flex-col overflow-y-scroll h-full w-full'}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearPolicy, leaveComponent, clearPage, clearAllCraApiVariables } = this.props;
    clearPolicy();
    clearAllCraApiVariables();
    leaveComponent();
    clearPage();
    await super.componentWillUnmount();
  }

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(this._startTracker);
      await this.stopTracker();
    }
  };

  protected _startTracker = async () => {
    const type = this.getTypeFromRoute();
    if (type == undefined) {
      await this.onChangeType('Individual', true);
      return;
    }

    const craQuery = query.QueryBuilder.select().from(CollectionNames.CRA).where(eq('id', type));
    this.craSubscription = subscribe.Formica.subscribe(CollectionNames.CRA, craQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.craSubscription]);
  };

  protected tracker = async () => {
    if (this.craSubscription?.ready()) {
      const { changePolicy, changePolicyDirty, changePolicyIsUpdate, setCraApiVariable, clearAllCraApiVariables } = this.props;
      const type = this.getTypeFromRoute();
      const craQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.CRA).where(eq('id', type)).build();
      const policy: PolicyJsonModel | undefined = await this.repository.find(craQuery);
      this.setState({ loading: false });
      clearAllCraApiVariables();

      if (policy == undefined) {
        changePolicyIsUpdate(false);
        changePolicy(ValueFactory.initializePolicy(type));
      } else {
        changePolicy(policy);

        if (policy.variables && policy.variables.length > 0) {
          for (const variable of policy.variables) {
            if (variable.type === 'API') {
              let processedMapping = null;
              if (variable.mapping) {
                try {
                  processedMapping = await this.expressionBridge.start(variable.mapping, {});
                } catch (e) {
                  console.error('Error processing mapping:', e);
                  processedMapping = variable.mapping;
                }
              }

              const apiVariableData: CraApiVariableSchema = {
                url: variable.url ?? '',
                method: variable?.method,
                parameters: variable?.parameters ?? [],
                headers: variable?.headers ?? {},
                body: variable?.body ?? '',
                auth: variable?.auth ?? 'No Auth',
                mapping: processedMapping,
                responseType: variable?.responseType || ResponseFormats.JSON,
                timeout: variable?.timeout || 100,
                properties: variable?.properties || { dataType: DataType.STRING },
              };
              setCraApiVariable(variable.name, apiVariableData);
            }
          }
        }
      }
      RoleHelper.setCurrentAccess(policy);
      changePolicyDirty(false);
      this.setState({ policyLoading: false, craUpdated: this.state.craUpdated + 1 });
    }
  };

  protected _stopTracker = async () => {
    await this.craSubscription?.stop();
    this.computation?.stop();
  };

  protected getTypeFromRoute = () => {
    const { location } = this.props.router;
    const { changePolicyIsUpdate } = this.props;

    const type = location.pathname.split('/')[2];
    if (type == undefined || type == '') {
      return undefined;
    }
    changePolicyIsUpdate(true);
    return type as CraType;
  };

  private onChangeType = async (assessor: CraType, resub?: boolean) => {
    const {
      router: { navigate, location },
      enterComponent,
    } = this.props;
    enterComponent({ component: ComponentName.POLICY, id: assessor });
    navigate(`/policy/${assessor}`);
    location.pathname = `/policy/${assessor}`;
    resub && this.resubscribeTracker();
  };

  private onSave = async () => {
    const {
      policy: { policy, isUpdate },
      changePolicyIsUpdate,
      lastCategory,
      craApiVariables,
    } = this.props;

    const updatedPolicy = { ...policy };

    if (craApiVariables && Object.keys(craApiVariables).length > 0) {
      for (const variableName of Object.keys(craApiVariables)) {
        const apiVariableData = craApiVariables[variableName];
        const variableIndex = updatedPolicy.variables.findIndex((v) => v.name === variableName);

        if (variableIndex !== -1) {
          const currentVariable = updatedPolicy.variables[variableIndex];
          if (currentVariable.type === 'API') {
            updatedPolicy.variables[variableIndex] = {
              ...updatedPolicy.variables[variableIndex],
              ...apiVariableData,
            };
          }
        }
      }
    }
    const isSaved = await this.craService.save(updatedPolicy, isUpdate, lastCategory);
    if (isSaved) {
      changePolicyIsUpdate(true);
    }
  };

  private onDeploy = (e: React.MouseEvent<HTMLElement>) => {
    const {
      policy: { policy },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.craService.save(policy, true, {} as EnrichedComponentCategory);
        await this.craService.deploy(policy);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
    });
  };

  private onUndeploy = async (e: React.MouseEvent<HTMLElement>) => {
    const {
      policy: { policy },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await confirmUndeployWithImpact(ComponentName.POLICY, [policy.assessor], () => this.craService.undeploy(policy.assessor));
      },
      message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
    });
  };

  private getListComponent = () => {
    const { loading, policyLoading } = this.state;
    if (loading || policyLoading) {
      return <CustomSkeleton />;
    }
    return <CraListComponent />;
  };

  protected getTabs = () => {
    return [
      {
        key: 'Variable',
        label: LanguageUtil.get({ id: `common.tabs.variable`, defaultMessage: 'Variable' }),
        content: this.getListComponent(),
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: `common.tabs.access`, defaultMessage: 'Access' }),
        content: <PolicyAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.POLICY, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: `common.tabs.audit`, defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.POLICY} documentName={this.props.policy.policy.assessor} canPreview />,
        visible: RoleHelper.canManage(ComponentName.POLICY, true) && this.environmentUtil.isAuditLogVisible(),
      },
    ];
  };

  protected changeItem = (item: PolicyJsonModel) => {
    const { changePolicy } = this.props;
    changePolicy(item);
    this.setState({ policyLoading: false });
  };
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  loading: boolean = true;
  policyLoading: boolean = true;
  craUpdated: number = 0;
}

const mapStateToProps = (state: RootState) => ({
  policy: state.policy as PolicyState,
  lastCategory: state.page.lastCategory,
  page: state.page as RootState['page'],
  craApiVariables: state.craApiVariable.craApiVariables,
});

const mapDispatchToProps = {
  enterComponent,
  leaveComponent,
  changePolicy,
  changePolicyIsUpdate,
  clearPolicy,
  changePolicyDependencies,
  clearPage,
  changePolicyDirty,
  setCraApiVariable,
  clearAllCraApiVariables,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(CraPolicyContainer));
